import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import SmartImage from '../components/SmartImage';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { GraduationCap, Clock, User, Users, Sparkles, ArrowRight, BookOpen } from 'lucide-react';

const Scholarships = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/enrollments/scholarships/public?only_available=true')
      .then(r => setCourses(r.data))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, []);

  const fmtPrice = (p) => typeof p === 'number' ? `$${p.toFixed(2)}` : '—';

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-secondary">
      <Helmet>
        <title>Becas disponibles · IAASP Academy</title>
        <meta name="description" content="Accede a cursos profesionales de IAASP Academy con beca completa. Consulta cupos disponibles en tiempo real y aplica hoy." />
        <meta property="og:title" content="Becas IAASP Academy" />
        <meta property="og:description" content="Cupos de beca limitados en nuestros cursos de protección, ciberseguridad y gestión de emergencias." />
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-amber-200/50">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-primary/5 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20 relative">
          <Badge className="bg-amber-500 text-white font-semibold mb-4 gap-1.5" data-testid="hero-badge">
            <Sparkles className="w-3.5 h-3.5" /> Programa de becas
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-primary mb-5 leading-tight" data-testid="scholarships-title">
            Cursos con <span className="text-amber-600">beca completa</span>
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Accede a formación profesional de primer nivel sin costo. Los cupos de beca son limitados
            y se asignan por orden de solicitud. Aplica ahora y transforma tu carrera.
          </p>
          <div className="flex items-center gap-6 mt-8 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <GraduationCap className="w-4 h-4 text-amber-600" />
              <span>{courses.length} curso{courses.length === 1 ? '' : 's'} con cupos activos</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="w-4 h-4 text-amber-600" />
              <span>
                {(() => {
                  const total = courses.reduce((a, c) => a + c.scholarships_remaining, 0);
                  return `${total} ${total === 1 ? 'cupo total disponible' : 'cupos totales disponibles'}`;
                })()}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12">
        {loading ? (
          <div className="text-center py-16 text-muted-foreground">Cargando becas...</div>
        ) : courses.length === 0 ? (
          <Card className="max-w-xl mx-auto" data-testid="no-scholarships-card">
            <CardContent className="p-10 text-center">
              <GraduationCap className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-primary mb-2">
                No hay cupos de beca disponibles por el momento
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Estamos trabajando en abrir nuevas convocatorias. Mientras tanto, puedes explorar nuestro catálogo de cursos.
              </p>
              <Link to="/courses">
                <Button className="gap-2" data-testid="go-courses-btn">
                  Ver catálogo completo <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="scholarship-grid">
            {courses.map((c) => {
              const urgency = c.scholarships_remaining <= 2;
              return (
                <Card
                  key={c.id}
                  className="group overflow-hidden hover:shadow-xl transition-all border-2 hover:border-amber-300"
                  data-testid={`scholarship-card-${c.id}`}
                >
                  <div className="relative aspect-video overflow-hidden bg-gray-100">
                    {c.image_url ? (
                      <SmartImage
                        src={c.image_url}
                        alt={c.title}
                        fallbackLabel={c.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-amber-500/15 to-primary/10 flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-amber-500/60" />
                      </div>
                    )}
                    <Badge
                      className={`absolute top-3 left-3 gap-1 font-semibold ${
                        urgency
                          ? 'bg-red-500 text-white animate-pulse'
                          : 'bg-amber-500 text-white'
                      }`}
                      data-testid={`remaining-badge-${c.id}`}
                    >
                      <Sparkles className="w-3 h-3" />
                      Quedan {c.scholarships_remaining} cupo{c.scholarships_remaining === 1 ? '' : 's'}
                    </Badge>
                    {!c.is_free && (
                      <Badge className="absolute top-3 right-3 bg-white/95 text-primary font-semibold line-through">
                        {fmtPrice(c.price)}
                      </Badge>
                    )}
                  </div>

                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-center gap-1.5 text-xs text-amber-700 font-bold uppercase tracking-wide">
                      <GraduationCap className="w-3.5 h-3.5" /> Beca 100%
                    </div>
                    <h3 className="font-heading font-bold text-primary text-lg leading-snug line-clamp-2">
                      {c.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{c.description}</p>

                    <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
                      {c.instructor_name && (
                        <span className="flex items-center gap-1 truncate">
                          <User className="w-3 h-3" /> {c.instructor_name}
                        </span>
                      )}
                      {c.hours && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {c.hours}h
                        </span>
                      )}
                    </div>

                    {/* Progress bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Cupos de beca</span>
                        <span className="font-semibold">
                          {c.scholarships_used}/{c.scholarships_max}
                        </span>
                      </div>
                      <div className="h-1.5 bg-amber-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all"
                          style={{ width: `${Math.min(100, (c.scholarships_used / c.scholarships_max) * 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Link to={`/courses/${c.slug}`} className="flex-1">
                        <Button variant="outline" className="w-full" data-testid={`view-course-${c.id}`}>
                          Ver curso
                        </Button>
                      </Link>
                      <Link to={`/enroll/${c.id}`} className="flex-1">
                        <Button className="w-full gap-1 bg-amber-600 hover:bg-amber-700" data-testid={`apply-scholarship-${c.id}`}>
                          Aplicar <ArrowRight className="w-3.5 h-3.5" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-12 border-t">
        <h2 className="text-2xl font-heading font-bold text-primary mb-6">Preguntas frecuentes</h2>
        <div className="space-y-4">
          <details className="group border rounded-xl p-4">
            <summary className="cursor-pointer font-semibold text-primary flex items-center justify-between">
              ¿Cómo se asignan las becas?
              <ArrowRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
            </summary>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Las becas se asignan por orden de solicitud hasta agotar los cupos disponibles.
              Cuando aplicas, tu solicitud queda <strong>pendiente de aprobación</strong> por el administrador del curso.
            </p>
          </details>
          <details className="group border rounded-xl p-4">
            <summary className="cursor-pointer font-semibold text-primary flex items-center justify-between">
              ¿Tengo que pagar algo?
              <ArrowRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
            </summary>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              No. Las becas de IAASP Academy cubren el <strong>100% del costo</strong> del curso. Solo necesitas completar
              el formulario de inscripción y seleccionar "🎓 Beca / Becado" como método de pago.
            </p>
          </details>
          <details className="group border rounded-xl p-4">
            <summary className="cursor-pointer font-semibold text-primary flex items-center justify-between">
              ¿Qué pasa si se agotan los cupos?
              <ArrowRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
            </summary>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Si todos los cupos de un curso están ocupados, la opción de beca se ocultará automáticamente.
              Puedes volver a verificar esta página más adelante o suscribirte a nuestras convocatorias.
            </p>
          </details>
        </div>
      </section>
    </div>
  );
};

export default Scholarships;
